import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { FreeType, paths } from '@app/constants';
import { ADD_TO_CART } from '@app/constants/add-to-cart';
import { State } from '@app/core/features';
import { AddToCartModel, ChartsStoreActions, ProductCart, ProductModel, selectors } from '@app/core/features/charts-store';
import { ChartsStoreService } from '@app/core/features/charts-store/charts-store.service';
import { RedeemVoucherRequest, RedeemVoucherResult } from '@app/core/features/charts-store/redeem-voucher/redeem-voucher.models';
import { RedeemVoucherService } from '@app/core/features/charts-store/redeem-voucher/redeem-voucher.service';
import { TouchService } from '@app/core/services/touch/touch.service';
import { Globals } from '@app/shared/models';
import { ShoppingCartService } from '@app/shared/services/shopping-cart/shopping-cart.service';
import { getRandomNumber, isMobileView } from '@app/utils';
import { environment } from '@env/index';
import { Store } from '@ngrx/store';
import { CookieService } from 'ngx-cookie-service';
import { ToastrService } from 'ngx-toastr';
import { takeUntil } from 'rxjs/operators';
import { BaseComponent } from '../base/base/base.component';

@Component({
  selector: 'app-add-to-cart',
  templateUrl: './add-to-cart.component.html',
  styleUrls: ['./add-to-cart.component.scss']
})
export class AddToCartDialogComponent extends BaseComponent implements OnInit {
  private config = environment;
  public addToCartModel: AddToCartModel[];
  private lang: string;
  public isLoading: boolean;
  private userId: string;
  private count: number;
  private listProductCart: ProductCart[];
  private productData: ProductModel[];
  public isMobileView = isMobileView();
  public unit: string;
  public typeView: number;
  public type = FreeType;
  show2ndChartButton = false;
  isReuse = false;

  constructor(
    public dialogRef: MatDialogRef<AddToCartDialogComponent>,
    private chartStoreService: ChartsStoreService,
    private cookieService: CookieService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private shoppingCartService: ShoppingCartService,
    private globals: Globals,
    // this service is in-use by template, please do not remove
    public touchService: TouchService,
    protected store: Store<State>,
    private redeemVoucherService: RedeemVoucherService,
    private router: Router,
    protected route: ActivatedRoute,
    private toastr: ToastrService,
  ) {
    super();
    this.lang = this.cookieService.get('lang');
    this.route.queryParams.subscribe(queryParams => {
      if (queryParams.typeView) {
        this.typeView = Number(queryParams.typeView);
      }
      if (queryParams.isReuse) {
        this.isReuse = Boolean(JSON.parse(queryParams.isReuse));
      }
    });
  }

  ngOnInit() {
    this.isLoading = true;
    this.userId = this.chartStoreService.getUserId();
    this.count = 0;
    this.initVariables();
    this.unit = this.chartStoreService.getCurrencyUnit(this.cookieService.get('lang'));
    this.getData();
    this.checkBackButton();
  }

  private checkBackButton() {
    if (this.typeView === this.type.FREE) {
      const latestAdded = JSON.parse(this.cookieService.get(ADD_TO_CART.LATEST_PRODUCT_ADDED));
      this.show2ndChartButton = this.isReuse ? false :
        latestAdded.find(x => x.userId === this.userId).product.length < environment.numberOfFreeChart;
    }
    if(this.typeView === 1)
    {
      // TwoCountriesFree voucher can choose one product
      this.show2ndChartButton =  false;
    }
  }

  private initVariables() {
    this.addToCartModel = [];
  }

  private getData() {
    const result = [];
    this.productData = [];
    this.listProductCart = this.data.userCookies.product;

    this.store
      .pipe(selectors.getData)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(data => {
        if (data && data.products) {
          this.productData = data.products;
          this.chartStoreService.checkEditListItemOnCart.next(getRandomNumber());
          if (this.data.userCookies.product.length > 0) {
            this.productData.forEach((item: ProductModel) => {
              const checkFreeData = this.chartStoreService
                .getFreeCart().find(x => x.sku === item.sku);
              const checkProductCart = this.listProductCart.find(x => x.sku === item.sku);

              /* Free base product */
              if (checkFreeData) {
                result.push(this.setAddToCartItem(item, true, checkFreeData.voucherCode));
                this.count++;
              }
              /* Normal base product */
              if (checkProductCart
                && ((checkProductCart.quantity > 1)
                  || (!checkFreeData && checkProductCart.quantity === 1))
              ) {
                const quantity = (checkProductCart.quantity > 1 && checkFreeData)
                  ? (checkProductCart.quantity - 1)
                  : checkProductCart.quantity;
                result.push(this.setAddToCartItem(item, false, null, quantity));
                this.count++;
              }

            });
            this.isLoading = false;
          } else {
            this.isLoading = false;
          }
        } else {
          this.store.dispatch(new ChartsStoreActions({ lang: this.cookieService.get('lang') }));
        }
      });
    this.addToCartModel = result;
  }

  private setAddToCartItem(
    item: ProductModel,
    isFree: boolean,
    voucherCode = null,
    quantity = 1
  ) {
    const addToCartModel: AddToCartModel = {
      sku: item.sku,
      name: item.name,
      premiumName: item.premiumName,
      isFree,
      quantity,
      voucherCode,
    };
    return addToCartModel;
  }

  public redeemVoucher(event) {
    event.preventDefault();

    const latestAdded = JSON.parse(this.cookieService.get(ADD_TO_CART.LATEST_PRODUCT_ADDED));
    const listSku = [];

    latestAdded[0].product.forEach(element => {
      if (!listSku.includes(element.sku)) {
        listSku.push(element.sku);
      }
    });

    const voucherRequest: RedeemVoucherRequest = {
      skus: listSku,
      code: this.data.voucherCode
    };

    this.chartStoreService.setListAddedCart([]);
    this.chartStoreService.setListFreeCart([]);
    this.dialogRef.close();

    this.redeemVoucherService.redeemVoucher(voucherRequest)
      .subscribe((response: RedeemVoucherResult) => {
        if (response.success) {
          this.router.navigate([paths.CHARTS_MANAGER], { replaceUrl: false });
        } else {
          this.toastr.error(response.message);
        }
      });
  }

  public closeDialog(event) {
    event.preventDefault();
    this.dialogRef.close();
  }

  private removeOnListCart(sku: string, isFree: boolean) {
    this.addToCartModel.splice(this.addToCartModel
      .findIndex(row => row.sku === sku && row.isFree === isFree), 1);
    this.count--;

    if (this.count === 0) {
      this.globals.isDisplayProgressSpinner = false;
      this.dialogRef.close();
    }
  }

  public remove(event, sku: string, isFree: boolean) {
    event.preventDefault();
    this.globals.isDisplayProgressSpinner = true;
    this.removeSku(sku, isFree);
    this.checkBackButton();
  }

  private removeSku(sku: string, isFree: boolean) {
    isFree
      ? this.shoppingCartService.removeFree(
        sku, this.listProductCart.find(x => x.sku === sku), this.userId)
      : this.shoppingCartService.removeNormal(sku, this.userId);

    this.removeOnListCart(sku, isFree);
  }

  public changeQuantityByOne(event, sku: string, isPlus: boolean, quantity: number) {
    event.preventDefault();

    const newQuantity = isPlus ? quantity + 1 : quantity - 1;
    this.addToCartModel
      .find(row => row.sku === sku && row.isFree === false).quantity = newQuantity;

    this.globals.isDisplayProgressSpinner = true;
  }
}
